import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  CustomerType,
  UpdateCustomerRequest,
  customersApi,
} from '@texas/api/endpoints/customersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { AlertDetails } from '@texas/components/shared/alert/AlertDetails';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BodyOverride } from '../../shared/BodyOverride';
import { ProjectContainer } from '../projects/ProjectContainer';
import { CustomerLogo } from './CustomerLogo';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { reactEvents } from '@bridge/reactEvents';
import { customerEvents } from '../events';
import { convertToEnum, getEnumValues } from '@texas/utils/helpers/enumHelpers';
import { ReactSelectOption } from '@texas/types';
import { TFunction } from 'i18next';

interface Props {
  customerId: number;
}

export function BrandManagement({ customerId }: Props) {
  const { t } = useTranslation();
  const toast = useToast();

  const {
    isOpen: isArchiveOpen,
    onClose: onArchiveClose,
    onOpen: onArchiveOpen,
  } = useDisclosure();

  const { data, refetch, loading, error, set } = useApiResource(
    customersApi.get,
  );

  const {
    request: updateRequest,
    error: updateError,
    loading: updateLoading,
  } = useApiRequest(customersApi.update);

  const {
    register,
    reset,
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<UpdateCustomerRequest>({
    defaultValues: {
      name: '',
      customerType: CustomerType.Brand,
      phone: '',
      email: '',
      officeAddress: '',
      website: '',
      notes: '',
      bcSampleApprovalRequired: false,
    },
  });

  useEffect(() => {
    if (!customerId) return;
    refetch(customerId);
  }, [customerId, refetch]);

  const onSubmit = async (formData: UpdateCustomerRequest) =>
    await request(updateRequest, [customerId, formData], (data) => {
      toast({
        title: t('general.updated'),
        status: 'success',
        isClosable: true,
      });
      set(data);
      reactEvents.customerNameChanged.dispatch({
        id: customerId,
        value: data.name,
      });
      customerEvents.brandUpdated.dispatch();
    });

  useEffect(() => {
    if (!data) return;

    reset({
      name: data.name,
      customerType: convertToEnum(CustomerType, data.customerType),
      phone: data.phone,
      email: data.email,
      officeAddress: data.officeAddress,
      website: data.website,
      notes: data.notes,
      bcSampleApprovalRequired: data.bcSampleApprovalRequired,
    });
  }, [data, reset]);

  return (
    <BodyOverride>
      <Card variant="dark" mb={4}>
        <CardBody>
          {error && <ErrorDetails error={error} />}
          {updateError && <ErrorDetails error={updateError} />}
          {loading && <SpinnerWithLabel label={t('general.loadingData')} />}

          {!loading && data && (
            <>
              {data.archived && (
                <AlertDetails type="warning" title={t('customer.archived')} />
              )}
              <Flex gap={4} mt={2} justify="space-between">
                <Flex direction="column" gap={2} w="100%">
                  <Text variant="header-medium">{t('general.project')}</Text>
                  <ProjectContainer
                    customerId={data.id}
                    brandName={data.name}
                    customerArchived={data.archived}
                    identifier={data.identifier}
                    finalizeContainer={false}
                  />
                </Flex>
                <CustomerLogo
                  customerId={data.id}
                  folderId={data.folderId}
                  fileId={data.logoId}
                />
              </Flex>
              <Text variant="header-medium">{t('general.general')}</Text>
              <form onSubmit={handleSubmit(onSubmit)}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mt={4}>
                  <FormControl isRequired={true} isInvalid={!!errors.name}>
                    <FormLabel>{t('general.name')}</FormLabel>
                    <Input
                      {...register('name', {
                        required: {
                          value: true,
                          message: t('errors.nameRequired', {
                            name: t('general.name'),
                          }),
                        },
                        maxLength: {
                          value: 100,
                          message: t('errors.maxLength', { count: 100 }),
                        },
                      })}
                      variant="texas"
                    />
                    <ErrorLabel text={errors.name?.message} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>{t('general.type')}</FormLabel>
                    <Controller
                      name="customerType"
                      control={control}
                      render={({ field }) => (
                        <TexasSelect
                          {...field}
                          required={true}
                          onChange={(selected) =>
                            field.onChange(selected?.value ?? null)
                          }
                          value={getCustomerTypeOption(field.value, t)}
                          options={getEnumValues(
                            CustomerType,
                          ).map<ReactSelectOption>((x) => ({
                            label: customerTypeFriendlyName(
                              x as CustomerType,
                              t,
                            ),
                            value: x,
                          }))}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl isInvalid={!!errors.phone}>
                    <FormLabel>{t('general.telephone')}</FormLabel>
                    <Input
                      {...register('phone', {
                        maxLength: {
                          value: 100,
                          message: t('errors.maxLength', { count: 100 }),
                        },
                      })}
                      variant="texas"
                    />
                    <ErrorLabel text={errors.phone?.message} />
                  </FormControl>

                  <FormControl isInvalid={!!errors.email}>
                    <FormLabel>{t('general.email')}</FormLabel>
                    <Input
                      {...register('email', {
                        maxLength: {
                          value: 100,
                          message: t('errors.maxLength', { count: 100 }),
                        },
                      })}
                      type="email"
                      variant="texas"
                    />
                    <ErrorLabel text={errors.email?.message} />
                  </FormControl>

                  <FormControl isInvalid={!!errors.officeAddress}>
                    <FormLabel>{t('general.officeAddress')}</FormLabel>
                    <Input
                      {...register('officeAddress', {
                        maxLength: {
                          value: 50,
                          message: t('errors.maxLength', { count: 50 }),
                        },
                      })}
                      variant="texas"
                    />
                    <ErrorLabel text={errors.officeAddress?.message} />
                  </FormControl>

                  <FormControl isInvalid={!!errors.website}>
                    <FormLabel>{t('general.website')}</FormLabel>
                    <Input
                      {...register('website', {
                        maxLength: {
                          value: 100,
                          message: t('errors.maxLength', { count: 100 }),
                        },
                      })}
                      variant="texas"
                    />
                    <ErrorLabel text={errors.website?.message} />
                  </FormControl>

                  <FormControl>
                    <Controller
                      name="bcSampleApprovalRequired"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Checkbox
                          isChecked={!!value}
                          onChange={(e) => onChange(e.target.checked)}
                        >
                          {t('customer.bcSampleApprovalRequired')}
                        </Checkbox>
                      )}
                    />
                  </FormControl>

                  <TexasInputWrapper
                    label={t('general.notes')}
                    error={errors.notes}
                  >
                    <Textarea
                      {...register('notes', {
                        maxLength: {
                          value: 4000,
                          message: t('errors.maxLength', { count: 4000 }),
                        },
                      })}
                      variant="unstyled"
                      maxH="250px"
                    />
                  </TexasInputWrapper>
                </SimpleGrid>

                <Flex ml="auto" pt={2} w="fit-content" gap={2}>
                  <Button
                    w="100%"
                    variant="texas-light"
                    onClick={onArchiveOpen}
                  >
                    {data.archived
                      ? t('general.restore')
                      : t('general.archive')}
                  </Button>
                  <SubmitButton
                    disabled={!isDirty}
                    loading={isSubmitting || updateLoading}
                  >
                    {t('general.update')}
                  </SubmitButton>
                </Flex>
              </form>
            </>
          )}
        </CardBody>
      </Card>
      {data && (
        <VerifyDialogWithRequest
          headerTitle={
            data.archived
              ? t('general.restore')
              : t('configuration.archive', { name: data.name })
          }
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          primaryRequest={
            data.archived ? customersApi.restore : customersApi.archive
          }
          args={[data.id]}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          onPerformed={(response) => {
            set((prev) =>
              prev ? { ...prev, archived: response.archived } : null,
            );
            if (response.archived) {
              reactEvents.customerArchived.dispatch(data.id);
            } else {
              reactEvents.customerRestored.dispatch(data.id);
            }
            onArchiveClose();
          }}
          onSuccessTitle={
            data.archived
              ? t('general.entityRestored', { name: data.name })
              : t('general.entityArchived', { name: data.name })
          }
        >
          {t('alert.areYouSure')}
        </VerifyDialogWithRequest>
      )}
    </BodyOverride>
  );
}

function customerTypeFriendlyName(customerType: CustomerType, t: TFunction) {
  switch (customerType) {
    case CustomerType.Brand:
      return t('general.brand');
    case CustomerType.Vendor:
      return t('general.vendor');
  }
}

function getCustomerTypeOption(
  customerType: CustomerType | null,
  t: TFunction,
): ReactSelectOption | null {
  if (customerType === null) return null;

  return {
    label: customerTypeFriendlyName(customerType, t),
    value: customerType,
  };
}
